import {
  Box,
  Card,
  CardBody,
  SimpleGrid,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Icon,
  HStack,
  useColorModeValue,
  Skeleton,
} from '@chakra-ui/react';
import { FiCpu, FiDatabase, FiActivity, FiLayers } from 'react-icons/fi';
import type { RuntimeStats } from '@/lib/sdk/profiling';

interface RuntimeStatsCardProps {
  stats?: RuntimeStats;
  isLoading?: boolean;
}

interface StatItemProps {
  label: string;
  value: string | number;
  helpText?: string;
  icon: React.ElementType;
  colorScheme?: string;
}

function StatItem({ label, value, helpText, icon, colorScheme = 'brand' }: StatItemProps) {
  const cardGradient = useColorModeValue(
    'linear(to-br, white, brand.50)',
    'linear(to-br, gray.800, gray.700)'
  );

  return (
    <Card variant="outline" bg="bg-surface" bgGradient={cardGradient}>
      <CardBody>
        <HStack spacing={4}>
          <Box
            p={3}
            borderRadius="lg"
            bg={`${colorScheme}.100`}
            color={`${colorScheme}.600`}
            _dark={{
              bg: `${colorScheme}.900`,
              color: `${colorScheme}.200`,
            }}
          >
            <Icon as={icon} boxSize={6} />
          </Box>
          <Stat>
            <StatLabel color="text-secondary">{label}</StatLabel>
            <StatNumber color="text-primary">{value}</StatNumber>
            {helpText && <StatHelpText color="text-muted">{helpText}</StatHelpText>}
          </Stat>
        </HStack>
      </CardBody>
    </Card>
  );
}

export function RuntimeStatsCard({ stats, isLoading }: RuntimeStatsCardProps) {
  if (isLoading) {
    return (
      <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} height="100px" borderRadius="lg" />
        ))}
      </SimpleGrid>
    );
  }

  if (!stats) {
    return null;
  }

  const formatBytes = (mb: number) => {
    if (mb >= 1024) {
      return `${(mb / 1024).toFixed(2)} GB`;
    }
    return `${mb} MB`;
  };

  const formatNs = (ns: number) => {
    if (ns >= 1_000_000) {
      return `${(ns / 1_000_000).toFixed(2)} ms`;
    }
    if (ns >= 1_000) {
      return `${(ns / 1_000).toFixed(2)} μs`;
    }
    return `${ns} ns`;
  };

  return (
    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
      <StatItem
        label="Goroutines"
        value={stats.goroutine_count.toLocaleString()}
        helpText={`${stats.num_cpu} CPUs`}
        icon={FiActivity}
        colorScheme="green"
      />
      <StatItem
        label="Heap Allocated"
        value={formatBytes(stats.heap_alloc_mb)}
        helpText={`${formatBytes(stats.heap_sys_mb)} reserved`}
        icon={FiDatabase}
        colorScheme="blue"
      />
      <StatItem
        label="Heap Objects"
        value={stats.heap_object_count.toLocaleString()}
        helpText={`Stack: ${formatBytes(stats.stack_in_use_mb)}`}
        icon={FiLayers}
        colorScheme="purple"
      />
      <StatItem
        label="GC Runs"
        value={stats.num_gc}
        helpText={`Last pause: ${formatNs(stats.gc_pause_ns)}`}
        icon={FiCpu}
        colorScheme="orange"
      />
    </SimpleGrid>
  );
}
